import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PayPalScriptProvider, PayPalButtons, PayPalMessages, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Navbar } from "../components/Navbar";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { pushEvent } from "../lib/analytics";
import { Check, ShieldCheck, Loader2, ArrowLeft, Sparkles } from "lucide-react";
import { PRICING_ENABLED } from "../config";

const PERKS = [
  "Full Ground School Academy — every premium module and lesson",
  "All future Ground School lessons included as they're released — no extra cost",
  "Full FAA Part 107 exam prep + practice quizzes",
  "Unlimited access to Skyler, your AI flight instructor",
  "Progress tracking, quiz scoring & achievement badges",
  "Access for as long as DronAcademy operates — pay once",
];

function PayPalArea({ processing, setProcessing, refreshUser, nav }) {
  const [{ isPending, isRejected }] = usePayPalScriptReducer();
  return (
    <>
      {isPending && (
        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground py-10 border border-dashed border-border/60 rounded-sm mb-2" data-testid="paypal-loading">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading secure checkout…
        </div>
      )}
      {isRejected && (
        <p className="text-sm text-destructive py-4" data-testid="paypal-load-error">
          Couldn't load the payment form. Please refresh the page and try again.
        </p>
      )}
      {!isPending && !isRejected && (
        <div className="mb-4 p-3 rounded-sm bg-background/40 border border-border/50" data-testid="paypal-paylater-messaging">
          <PayPalMessages
            amount={89}
            style={{ layout: "text", logo: { type: "inline" }, text: { color: "white", size: 13 } }}
          />
        </div>
      )}
      <PayPalButtons
        style={{ layout: "vertical", color: "gold", shape: "pill", label: "pay" }}
        disabled={processing}
        createOrder={async () => {
          const { data } = await api.post("/payments/create-order");
          return data.order_id;
        }}
        onApprove={async (data) => {
          setProcessing(true);
          try {
            const res = await api.post(`/payments/capture-order/${data.orderID}`);
            if (res.data.has_paid) {
              await refreshUser();
              pushEvent("purchase", {
                ecommerce: { transaction_id: data.orderID, value: 89.00, currency: "USD" },
              });
              if (typeof window.fbq === "function") {
                window.fbq("track", "Purchase", {
                  value: 89.00,
                  currency: "USD",
                  content_name: "DronAcademy Full Course",
                });
              }
              toast.success("Payment complete — course unlocked!");
              nav("/dashboard");
            } else {
              toast.error("Payment could not be confirmed.");
            }
          } catch (e) {
            toast.error("Payment could not be completed.");
          } finally {
            setProcessing(false);
          }
        }}
        onError={() => toast.error("Something went wrong with PayPal. Please try again.")}
      />
    </>
  );
}

export default function Checkout() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const [processing, setProcessing] = useState(false);
  const clientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  const paypalOptions = {
    clientId,
    currency: "USD",
    intent: "capture",
    components: "buttons,messages",
    enableFunding: "paylater",
    // buyer-country is a SANDBOX-ONLY override for Pay Later eligibility testing.
    // It must be unset in production (live SDK rejects it); driven by env so prod ships without it.
    ...(process.env.REACT_APP_PAYPAL_BUYER_COUNTRY
      ? { buyerCountry: process.env.REACT_APP_PAYPAL_BUYER_COUNTRY }
      : {}),
  };

  if (!PRICING_ENABLED && !user?.has_paid) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="max-w-lg mx-auto px-6 pt-20 text-center">
          <div className="h-16 w-16 mx-auto rounded-sm bg-primary/15 flex items-center justify-center glow-cyan mb-5">
            <Sparkles className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-2">Full course access is coming soon</h1>
          <p className="text-muted-foreground mb-8">
            Your free training is fully available now — keep flying through Ground School. We'll let you know when the complete course opens.
          </p>
          <Link to="/dashboard">
            <button className="h-11 px-6 rounded-sm bg-primary text-primary-foreground font-semibold" data-testid="checkout-back-dashboard">
              Back to Dashboard
            </button>
          </Link>
        </main>
      </div>
    );
  }

  if (user?.has_paid) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="max-w-lg mx-auto px-6 pt-20 text-center">
          <div className="h-16 w-16 mx-auto rounded-sm bg-primary/15 flex items-center justify-center glow-cyan mb-5">
            <ShieldCheck className="h-8 w-8 text-primary" />
          </div>
          <h1 className="font-display text-3xl font-bold mb-2">You're all set, pilot</h1>
          <p className="text-muted-foreground mb-8">You already have full course access.</p>
          <Link to="/dashboard">
            <button className="h-11 px-6 rounded-sm bg-primary text-primary-foreground font-semibold" data-testid="go-dashboard-btn">
              Go to Dashboard
            </button>
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="checkout-back">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>

        <div className="grid lg:grid-cols-2 gap-6">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
            className="p-7 rounded-sm border border-border/70 bg-card">
            <span className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-accent mb-4">
              <Sparkles className="h-3.5 w-3.5" /> Full Access
            </span>
            <h1 className="font-display text-3xl font-bold mb-2">DronAcademy Course</h1>
            <p className="text-slate-400 mb-6">Everything you need to go from first hover to FAA-certified commercial pilot.</p>
            <div className="flex items-baseline gap-2 mb-6">
              <span className="font-mono text-4xl font-bold text-primary">$89</span>
              <span className="text-sm text-muted-foreground">paid once · access for as long as DronAcademy operates</span>
            </div>
            <ul className="space-y-3">
              {PERKS.map((p) => (
                <li key={p} className="flex items-start gap-3 text-sm text-slate-300">
                  <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
            <p className="text-[11px] text-muted-foreground/80 mt-5 leading-relaxed" data-testid="checkout-terms-note">
              Your $89 covers Ground School and every future Ground School lesson, included as they're released, for as long as DronAcademy operates. Other academies, coaching, and live events are separate and not included.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="p-7 rounded-sm border border-border/70 bg-card" data-testid="paypal-panel">
            <h2 className="font-display text-xl font-bold mb-1">Complete your purchase</h2>
            <p className="text-sm text-muted-foreground mb-6">Secure checkout with PayPal or card.</p>

            {processing && (
              <div className="flex items-center gap-2 text-sm text-primary mb-4">
                <Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment…
              </div>
            )}

            {clientId ? (
              <PayPalScriptProvider options={paypalOptions}>
                <PayPalArea processing={processing} setProcessing={setProcessing} refreshUser={refreshUser} nav={nav} />
              </PayPalScriptProvider>
            ) : (
              <p className="text-sm text-destructive">PayPal is not configured.</p>
            )}

            <p className="text-xs text-muted-foreground mt-5 flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5" /> Payments processed securely by PayPal.
            </p>
          </motion.div>
        </div>
      </main>
    </div>
  );
}
